import React, { useState } from 'react';

import { X } from 'lucide-react';

import { useGetCustomRatesListQuery, useUploadStatementMutation } from '../api';
import { useAppDispatch, useTypedSelector } from '../store';
import { clearDirectRates } from '../store/configSlice';
import {
	STATEMENT_UPLOAD_ACCEPT,
	STATEMENT_UPLOAD_HINT,
	sortStatementPhotos,
	statementFilesError,
} from '../utils/statementUploadFormats';

import { Button, FileUploadDropzone, SelectInput } from './common';
import CustomizeFeeStructureModal from './CustomizeFeeStructureModal';
import ViewRatesModal from './settings/ViewRatesModal';

import type { RiskTier } from '../api';
import type { UploadConfig } from '../types';

// UI-level rate types. 'FLAT_RATE' is a UI-only value: it maps to the
// backend's 'FLAT_TIERED' rate_plan_type on submit (the backend has no
// dedicated FLAT_RATE). It exists to distinguish the flat-rate PDF flow from
// the Stripe transactional Excel/CSV ('FLAT_TIERED') flow — both run on the
// platform engine, but the PDF is read by the Deep Agent and the spreadsheet
// by the in-process code extractor.
type UiRateType = 'IC_PLUS' | 'TIERED' | 'FLAT_TIERED' | 'FLAT_RATE';

// Helper function to get human-readable labels for rate types
const getRateTypeLabel = (type: UiRateType | ''): string => {
	switch (type) {
		case 'IC_PLUS':
			return 'Interchange Plus';
		case 'TIERED':
			return 'Tiered';
		case 'FLAT_TIERED':
			return 'Flat Rate (Transactional)';
		case 'FLAT_RATE':
			return 'Flat Rate';
		default:
			return type;
	}
};

interface StatementUploadProps {
	onClose: () => void;
	onSuccess: (uploadedFileId?: string) => void;
	uploadConfig?: UploadConfig;
	mfe?: boolean;
}

const StatementUpload: React.FC<StatementUploadProps> = ({
	onClose,
	onSuccess,
	uploadConfig,
	mfe = false,
}) => {
	const dispatch = useAppDispatch();
	const directRates = useTypedSelector((state) => state.config.directRates);

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [dragActive, setDragActive] = useState(false);
	const [error, setError] = useState('');
	const [selectedConfigId, setSelectedConfigId] = useState('');
	const [selectedRateTypes, setSelectedRateTypes] = useState<UiRateType[]>([
		'IC_PLUS',
	]);
	const [selectedRiskTier, setSelectedRiskTier] = useState<RiskTier>('LOW');
	const [showViewRatesModal, setShowViewRatesModal] = useState(false);
	const [showCustomizeModal, setShowCustomizeModal] = useState(false);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

	// Flat Tiered specific fields
	const [processorName, setProcessorName] = useState('');
	const [businessType, setBusinessType] = useState('');
	// Target output format for cross-type conversion (e.g., FLAT_TIERED -> IC_PLUS)
	const [targetRateType, setTargetRateType] = useState<UiRateType | ''>('');

	const [uploadStatement, { isLoading: uploading }] =
		useUploadStatementMutation();

	// Two explicit flat-rate dropdown items, each with its own input shape:
	//  - 'FLAT_TIERED' — a Stripe transactional export (Excel/CSV).
	//  - 'FLAT_RATE' — a processor's flat-rate PDF statement. UI-only:
	//    submits as rate_plan_type='FLAT_TIERED'.
	// Both run on the platform engine; needs a business type (and a payment
	// processor for the transactional export).
	const isFlatTransactional = selectedRateTypes.includes('FLAT_TIERED');
	const isFlatRatePdf = selectedRateTypes.includes('FLAT_RATE');
	// Either flat dropdown item shows the shared "flat details" panel.
	const isAnyFlat = isFlatTransactional || isFlatRatePdf;

	// Fetch config list
	const { data: configsResponse, isLoading: isLoadingConfigs } =
		useGetCustomRatesListQuery({
			page: 1,
			page_size: 50, // Fetch all configs
		});
	const configs = configsResponse?.data || [];

	// Set default selection to first config if available
	React.useEffect(() => {
		if (configs.length > 0 && !selectedConfigId) {
			const defaultConfig = configs.filter((config) => config.is_default)[0];
			setSelectedConfigId(defaultConfig?.id || '');
		}
	}, [configs, selectedConfigId]);

	// Get available output format options based on input statement type
	// Currently only FLAT_TIERED -> IC_PLUS conversion is supported
	const inputStatementType = selectedRateTypes[0];
	const getOutputFormatOptions = () => {
		// Both flat items are FLAT_TIERED underneath, so they expose the same
		// conversion options. The "same as input" option keeps the input value
		// (FLAT_TIERED or FLAT_RATE) so the SelectInput matches an option and the
		// no-op target is suppressed on submit (target === input).
		if (
			inputStatementType === 'FLAT_TIERED' ||
			inputStatementType === 'FLAT_RATE'
		) {
			return [
				{
					value: inputStatementType,
					label: 'Flat Rate (Same as input)',
					isDefault: true,
				},
				{
					value: 'IC_PLUS',
					label: 'Interchange Plus (Convert)',
					isDefault: false,
				},
			];
		}
		// Other types don't support conversion yet - show only the input type
		return [
			{
				value: inputStatementType,
				label: getRateTypeLabel(inputStatementType),
				isDefault: true,
			},
		];
	};

	const outputFormatOptions = getOutputFormatOptions();
	const hasMultipleOutputOptions = outputFormatOptions.length > 1;

	// Auto-select target rate type to match input statement type when it changes
	React.useEffect(() => {
		// Default target to same as input statement type
		setTargetRateType(inputStatementType as UiRateType);
	}, [inputStatementType]);

	// Auto-select defaults for the flat dropdown items.
	React.useEffect(() => {
		if (selectedRateTypes.includes('FLAT_TIERED')) {
			// Stripe transactional Excel path needs a processor + business type.
			setProcessorName((prev) => prev || 'Stripe');
			setBusinessType((prev) => prev || 'retail');
		} else if (selectedRateTypes.includes('FLAT_RATE')) {
			// Flat-rate PDF path needs only a business type — no processor.
			setBusinessType((prev) => prev || 'retail');
		}
	}, [selectedRateTypes]);

	const handleDrag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			handleFiles(Array.from(e.dataTransfer.files));
		}
	};

	const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			handleFiles(Array.from(e.target.files));
		}
	};

	const handleFiles = (picked: File[]) => {
		const files = picked.length > 1 ? sortStatementPhotos(picked) : picked;
		const file = files[0];
		if (!file) return;
		if (files.length > 1 && isFlatTransactional) {
			setError('Upload one Excel or CSV file at a time.');
			return;
		}
		// Excel/CSV only for the legacy Stripe-export flat path (FLAT_TIERED);
		// every other case (IC+/Tiered, and the flat-rate PDF item) takes a PDF.
		if (isFlatTransactional) {
			const validTypes = [
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
				'application/vnd.ms-excel', // .xls
				'text/csv', // .csv
			];
			const fileExtension = file.name.toLowerCase().split('.').pop();
			const isValidType =
				validTypes.includes(file.type) ||
				['xlsx', 'xls', 'csv'].includes(fileExtension || '');

			if (!isValidType) {
				setError('Please select an Excel (.xlsx) or CSV file only.');
				return;
			}
		} else {
			const filesError = statementFilesError(files);
			if (filesError) {
				setError(filesError);
				return;
			}
		}
		if (files.some((f) => f.size > 10 * 1024 * 1024)) {
			// 10MB limit per file
			setError('Each file must be less than 10MB.');
			return;
		}
		setError('');
		setSelectedFile(file);
		setSelectedFiles(files);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!selectedFile) {
			return;
		}

		// Validate flat required fields. The flat-rate PDF path needs only a
		// business type; the Excel/CSV (Stripe export) path also needs a
		// payment processor.
		if (isAnyFlat) {
			if (isFlatTransactional && !processorName) {
				setError('Please select a payment processor for flat rate statements.');
				return;
			}
			if (!businessType) {
				setError('Please select a business type for flat statements.');
				return;
			}
		}

		setError('');

		try {
			const formData = new FormData();
			(selectedFiles.length ? selectedFiles : [selectedFile]).forEach((f) =>
				formData.append('file', f),
			);
			formData.append('statement_type', 'vendor_statement');

			// Append rate plan type (use first selected type for backward
			// compatibility). 'FLAT_RATE' is UI-only — the backend has no such
			// type, so it submits as 'FLAT_TIERED'.
			const submittedRatePlanType =
				selectedRateTypes[0] === 'FLAT_RATE'
					? 'FLAT_TIERED'
					: selectedRateTypes[0];
			formData.append('rate_plan_type', submittedRatePlanType);

			// Append selected risk tier for revenue calculation
			formData.append('selected_risk_tier', selectedRiskTier);

			// Extraction engine is no longer selectable — the backend defaults
			// everything to the platform engine.

			// Append target_rate_type for cross-type conversion (applicable to all statement types)
			if (targetRateType && targetRateType !== selectedRateTypes[0]) {
				// Only send target_rate_type if it's different from input type (conversion requested)
				formData.append('target_rate_type', targetRateType);
			}

			// Append flat-specific fields. business_type applies to both flat
			// items; processor_name only to the Excel (Stripe transactional
			// export) FLAT_TIERED path — never the flat-rate PDF path.
			if (isAnyFlat) {
				formData.append('business_type', businessType);
				if (isFlatTransactional) {
					formData.append('processor_name', processorName);
				}
			}

			// If direct_rates is available, use it instead of custom_rates_id
			if (directRates) {
				formData.append('direct_rates', JSON.stringify(directRates));
				// Clear direct_rates after use
				dispatch(clearDirectRates());
			} else if (selectedConfigId) {
				formData.append('custom_rates_id', selectedConfigId);
			}

			const result = await uploadStatement({
				formData,
				uploadConfig,
			}).unwrap();

			// Store the fileId for potential customization
			setSelectedFileId(result.file_id);
			setSelectedFile(null);
			setSelectedFiles([]);

			onSuccess(result.file_id);
			onClose();
		} catch (err: any) {
			setError(err?.data?.message || 'Upload failed. Please try again.');
		}
	};

	const removeFile = () => {
		setSelectedFile(null);
		setSelectedFiles([]);
		setError('');
	};

	const handleCancel = () => {
		// Clear any customized direct rates when canceling
		if (directRates) {
			dispatch(clearDirectRates());
		}
		onClose();
	};

	return (
		<div
			className={`fixed inset-0 ${mfe ? '' : 'bg-black/40'} flex items-center justify-center z-50 p-4`}
		>
			<div className="relative w-[800px] max-w-full bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-200 shadow-xl flex flex-col overflow-hidden">
				{/* Header */}
				<div className="flex items-center gap-2.5 p-5">
					<div className="flex-1 text-black text-lg font-medium leading-tight">
						Upload {isFlatTransactional ? 'Transactional' : 'PDF'} Statement
					</div>
					<Button
						variant="ghost"
						size="sm"
						onClick={handleCancel}
						className="w-6 h-6 p-0 text-neutral-500 hover:text-neutral-700"
						icon={X}
					/>
				</div>

				{/* Content */}
				<div className="flex-1 px-5 pb-5">
					<div className="flex h-full flex-col gap-5">
						{/* Fee Structure Config Selection */}
						<div className="flex flex-row gap-2 align-center">
							{/* Rate Plan Type Selection */}
							<SelectInput
								label="Statement Type"
								value={selectedRateTypes[0] || 'IC_PLUS'}
								options={[
									{ label: 'Interchange Plus', value: 'IC_PLUS' },
									{ label: 'Tiered', value: 'TIERED' },
									{
										label: 'Flat Rate (Transactional)',
										value: 'FLAT_TIERED',
									},
									{ label: 'Flat Rate', value: 'FLAT_RATE' },
								].map((config) => ({
									value: config.value || '',
									label: config.label,
									isDefault: config.value === 'IC_PLUS',
								}))}
								onChange={(value) => {
									const nextType = value as UiRateType;
									setSelectedRateTypes([nextType]);
									// Keep the picked file: every statement type accepts the same formats.
									setError('');
									// Reset flat fields
									setProcessorName('');
									setBusinessType('retail');
								}}
								placeholder="Select Statement Type"
								selectClassName="bg-white"
								containerClassName="w-2/3"
								fullWidth={false}
							/>
							<SelectInput
								label="Select Fee Structure Config"
								value={selectedConfigId}
								options={configs.map((config) => ({
									value: config.id || '',
									label: config.name,
									isDefault: config.is_default,
									isCustom: config.name.toLowerCase().includes('custom'),
								}))}
								onChange={setSelectedConfigId}
								onViewRates={() => setShowViewRatesModal(true)}
								viewRatesLabel="View Rates"
								disabled={uploading || isLoadingConfigs}
								placeholder={
									isLoadingConfigs
										? 'Loading configurations...'
										: 'Select a configuration'
								}
								selectClassName="bg-white"
								viewRatesButtonClassName="outline outline-1 outline-offset-[-1px] outline-neutral-200"
							/>
						</div>

						{/* Output Format Selection - Show for all statement types */}
						<div className="flex flex-row gap-2 align-center">
							<SelectInput
								label="Output Format"
								value={targetRateType || inputStatementType}
								options={outputFormatOptions}
								onChange={(value) => setTargetRateType(value as UiRateType)}
								placeholder="Select output format"
								selectClassName="bg-white"
								disabled={uploading || !hasMultipleOutputOptions}
								helperText={
									hasMultipleOutputOptions
										? 'Select the pricing format for the comparison results'
										: 'No conversion available for this statement type'
								}
								containerClassName="w-1/2"
								fullWidth={false}
							/>
						</div>

						{/* Risk Tier Selection for Revenue Calculation */}
						<div className="flex flex-col gap-2">
							<SelectInput
								label="Merchant Risk Tier (for ISO profit analysis)"
								value={selectedRiskTier}
								options={[
									{ value: 'LOW', label: 'Low Risk', isDefault: true },
									{ value: 'MEDIUM', label: 'Medium Risk', isDefault: false },
									{ value: 'HIGH', label: 'High Risk', isDefault: false },
								]}
								onChange={(value) => setSelectedRiskTier(value as RiskTier)}
								placeholder="Select Risk Tier"
								selectClassName="bg-white"
								disabled={uploading}
								helperText="Used to calculate agent profit margins based on Schedule A buy rates (Coastal Pay only)"
							/>
						</div>

						{/* Flat Fields — shown for both flat dropdown items */}
						{isAnyFlat && (
							<div className="flex flex-col gap-4 p-4 bg-blue-50 rounded-md border border-blue-200">
								<div className="text-sm font-medium text-blue-900">
									{isFlatTransactional
										? 'Transaction Statement Details'
										: 'Flat-Rate Statement Details'}
								</div>
								<div className="flex flex-col gap-4">
									{isFlatTransactional && (
										<SelectInput
											label="Payment Processor *"
											value={processorName}
											options={[
												{ value: 'Stripe', label: 'Stripe', isDefault: true },
											]}
											onChange={setProcessorName}
											placeholder="Select payment processor"
											selectClassName="bg-white"
											disabled={true}
											helperText="Currently only Stripe transaction exports are supported"
										/>
									)}
									<SelectInput
										label="Business Type *"
										value={businessType}
										options={[
											{
												value: 'retail',
												label: 'Retail / Online / CNP',
												isDefault: true,
											},
											{
												value: 'restaurant',
												label: 'Restaurant',
												isDefault: false,
											},
										]}
										onChange={setBusinessType}
										placeholder="Select business type"
										selectClassName="bg-white"
										disabled={uploading}
										helperText="Select your primary business type for accurate interchange estimates"
									/>
								</div>
							</div>
						)}

						{/* File Upload Area */}
						<div className="flex-1">
							<FileUploadDropzone
								title="Drag and drop file here"
								description={
									isFlatTransactional
										? 'Excel (.xlsx) or CSV files with required columns: Status, Amount, Currency, Fee, Card Brand. Optional: Card Funding, Card Issue Country (max 10MB)'
										: STATEMENT_UPLOAD_HINT
								}
								buttonText="Choose File"
								accept={
									isFlatTransactional
										? '.xlsx,.xls,.csv'
										: STATEMENT_UPLOAD_ACCEPT
								}
								maxSize={10}
								onFileSelect={(f) => handleFiles([f])}
								onFilesSelect={handleFiles}
								selectedFiles={selectedFiles}
								onFileRemove={removeFile}
								selectedFile={selectedFile}
								dragActive={dragActive}
								onDragEnter={handleDrag}
								onDragLeave={handleDrag}
								onDragOver={handleDrag}
								onDrop={handleDrop}
								disabled={uploading}
								error={error}
								containerClassName="outline-zinc-400"
								iconClassName="text-[#ACB1B7]"
								titleClassName="text-black"
								descriptionClassName="text-zinc-500"
								buttonClassName="bg-gray-100 hover:bg-gray-200"
							/>
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="flex items-center gap-2.5 p-5">
					<Button
						variant="outline"
						onClick={handleCancel}
						disabled={uploading}
						className="flex-1 h-10 px-5 bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-200 text-zinc-800 text-sm font-medium"
					>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="primary"
						onClick={handleSubmit}
						disabled={uploading || !selectedFile}
						loading={uploading}
						className="flex-1 h-10 px-5 bg-primary rounded-md text-white text-sm font-medium"
					>
						Upload Statement
					</Button>
				</div>
			</div>

			{/* ViewRates Modal */}
			{showViewRatesModal && selectedConfigId && (
				<ViewRatesModal
					configId={selectedConfigId}
					onClose={() => setShowViewRatesModal(false)}
					onCustomize={(fileId) => {
						setSelectedFileId(fileId || null);
						setShowCustomizeModal(true);
						setShowViewRatesModal(false);
					}}
				/>
			)}

			{/* Customize Fee Structure Modal */}
			{showCustomizeModal && selectedConfigId && (
				<CustomizeFeeStructureModal
					isOpen={showCustomizeModal}
					onClose={() => setShowCustomizeModal(false)}
					onConfirm={() => {
						setShowCustomizeModal(false);
						setShowViewRatesModal(false);
					}}
					configId={selectedConfigId}
					fileId={selectedFileId || ''}
					context="upload"
				/>
			)}
		</div>
	);
};

export default StatementUpload;
